import React from 'react';
import { useInView } from '../hooks/useInView';

type Kind = 'inventory' | 'register' | 'app';

const HOSTS: Record<Kind, string> = {
  inventory: 'stock.northside.app',
  register: 'counter.northside.app',
  app: 'Order ahead',
};

/**
 * Illustrative client prototypes, drawn as real product windows: the same
 * dark chrome and hairline grid as the hero report, one accent, tabular
 * numbers. Each window finishes one small change once it scrolls into view
 * (a reorder goes out, an order moves to Ready, an item lands in the cart),
 * then holds. Reduced motion renders the finished frame.
 */
const ClientBuildVisual: React.FC<{ kind: Kind }> = ({ kind }) => {
  const { ref, inView } = useInView<HTMLDivElement>(0.5);
  const cls = `client-ui client-ui--${kind}${inView ? ' is-in' : ''}`;

  return (
    <div className={cls} ref={ref} role="img" aria-label={`Illustrative ${kind} prototype interface`}>
      {kind !== 'app' && (
        <div className="client-ui__window">
          <div className="client-ui__chrome">
            <span /><span /><span />
            <b>{HOSTS[kind]}</b>
          </div>

          {kind === 'inventory' && (
            <div className="client-ui__screen">
              <div className="client-ui__head">
                <strong>Inventory</strong>
                <span className="client-ui__pill client-ui__pill--act">1 to reorder</span>
              </div>
              <div className="client-ui__table-head"><span>Item</span><span>On hand</span><span>Status</span></div>
              <div className="client-ui__table-row client-ui__table-row--hot"><span>Matcha powder</span><b>4.2 kg</b><em><i>Low</i><i>Reorder sent</i></em></div>
              <div className="client-ui__table-row"><span>Oat milk</span><b>18</b><em className="is-ok">In stock</em></div>
              <div className="client-ui__table-row"><span>Cold brew</span><b>32</b><em className="is-ok">In stock</em></div>
              <div className="client-ui__table-row"><span>Cups, 16 oz</span><b>240</b><em className="is-ok">In stock</em></div>
            </div>
          )}

          {kind === 'register' && (
            <div className="client-ui__screen client-ui__register">
              <div className="client-ui__ticket">
                <small>Order #014 · pickup</small>
                <strong>2 × Iced matcha</strong>
                <strong>1 × Strawberry matcha</strong>
                <div className="client-ui__total"><span>Total</span><b>$24.00</b></div>
              </div>
              <div className="client-ui__rail" aria-hidden="true">
                <span>New</span>
                <span className="client-ui__rail-prep">Preparing</span>
                <span className="client-ui__rail-ready">Ready</span>
              </div>
            </div>
          )}
        </div>
      )}

      {kind === 'app' && (
        <div className="client-ui__phone">
          <div className="client-ui__statusbar"><span>9:41</span><span /></div>
          <small>{HOSTS.app}</small>
          <strong>Your usual, ready when you are.</strong>
          <div className="client-ui__product"><span>Iced matcha</span><b>$7.00</b></div>
          <div className="client-ui__button"><i>Add to order</i><i>Added · 1</i></div>
        </div>
      )}
    </div>
  );
};

export default React.memo(ClientBuildVisual);
